import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { isNil } from 'lodash/fp';
import { constVoid } from 'fp-ts/function';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { TooltippedSpan } from '@mews-commander/ui';
import { IconName, Toggle } from '@optimus-web/core';
import { Reservation } from '@mews-commander/data-access';
import {
    getCurrentEnterpriseReservationAssignedResourceId,
    getCurrentEnterpriseReservationResourceLocked,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { updateLockedStatus } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { withActiveWindowLoader } from '../../../core';

interface OwnProps {
    reservationId: Reservation['id'];
    onSuccess: () => void;
}
interface DispatchProps {
    toggleLocked: (lockedState: boolean) => Promise<void>;
}
interface StateProps {
    isLocked: boolean | undefined;
    assignedResourceId: Reservation['assignedResourceId'] | undefined;
}
type Props = OwnProps & DispatchProps & StateProps;

const ReservationLockedToggle: React.FC<Props> = ({ isLocked, assignedResourceId, toggleLocked }) => {
    if (isNil(assignedResourceId) || isNil(isLocked)) {
        return null;
    }

    const handleOnChange = async () => toggleLocked(!isLocked);
    const tooltipKey = isLocked ? TextKey.Unlock : TextKey.Lock;

    return (
        <TooltippedSpan tooltip={<LocalizedText textKey={tooltipKey} />}>
            <Toggle
                checked={isLocked}
                // eslint-disable-next-line react/jsx-handler-names
                onBackgroundIcon={IconName.Lock}
                offBackgroundIcon={IconName.Unlock}
                onChange={handleOnChange}
            />
        </TooltippedSpan>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    createStructuredSelector({
        isLocked: (state: ApplicationState, { reservationId }: OwnProps) =>
            getCurrentEnterpriseReservationResourceLocked(state, { id: reservationId }),
        assignedResourceId: (state: ApplicationState, { reservationId }: OwnProps) =>
            getCurrentEnterpriseReservationAssignedResourceId(state, { id: reservationId }),
    }),
    (dispatch: CommanderThunkDispatch, { reservationId, onSuccess = constVoid }) => ({
        toggleLocked: async (lockedStatus: boolean) =>
            withActiveWindowLoader(() =>
                dispatch(updateLockedStatus({ reservationIds: [reservationId], lockedStatus }))
            ).then(onSuccess),
    })
)(ReservationLockedToggle);
